import { NgClass, NgStyle, TitleCasePipe } from "@angular/common";
import { Component, EventEmitter, Input, Output } from "@angular/core";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { Data } from "@angular/router";
import { IonicModule } from "@ionic/angular";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import * as moment from "moment";
import { SafeUrlPipe } from "src/app/pipe/syndicates-pipe/safe-url.pipe";
import { ExpandableComponent } from "../../../components/expandable/expandable.component";
import { SyndicatePricePipe } from "../../../pipe/syndicate-price.pipe";
import { SyndicateproductPipe } from "../../../pipe/syndicates-pipe/syndicateproduct.pipe";
import { AuthService } from "@core/auth.service";

@Component({
  selector: "app-syndicate-card",
  templateUrl: "./syndicate-card.component.html",
  styleUrl: "./syndicate-card.component.scss",
  standalone: true,
  imports: [
    IonicModule,
    NgClass,
    NgStyle,
    ExpandableComponent,
    MatProgressSpinner,
    TitleCasePipe,
    SyndicatePricePipe,
    SyndicateproductPipe,
    TranslatePipe,
    SafeUrlPipe,
  ],
})
export class SyndicateCardComponent {
  @Input() syndicate?: Data;
  @Input() goldRingWidgetDetails?: Data;
  @Input() preferredCurrency?: string;
  @Input() currencySymbol?: string;
  @Output() openSyndicateSettingEvent = new EventEmitter<Data>();
  @Output() updateProductForManagedSyndicateEvent = new EventEmitter<Data>();
  @Output() clickOfferBannerEvent = new EventEmitter<Data>();
  @Output() openTicketsEvent = new EventEmitter<Data>();
  @Output() routeToCreateSyndicateEvent = new EventEmitter<Data>();
  @Output() getPrivateSyndicateEvent = new EventEmitter<Data>();
  @Output() showPaymentOptionsEvent = new EventEmitter<Data>();
  @Output() chatNowEvent = new EventEmitter<Data>();
  @Output() openSyndicateMembersEvent = new EventEmitter<Data>();

  constructor(private translateService: TranslateService, private authService: AuthService) {}

  showBuyAgainUI(syndicate: Data): boolean {
    const syndicateEndDate = syndicate?.syndicate_end_date;
    
    // Comprehensive validation: ensure syndicate and syndicateEndDate are valid
    if (!syndicate || syndicateEndDate === undefined || syndicateEndDate === null || typeof syndicateEndDate !== 'string') {
      return false;
    }
    
    // Additional check: ensure the date string is not empty or whitespace-only
    const trimmedEndDate = syndicateEndDate.trim();
    if (trimmedEndDate === '') {
      return false;
    }
    
    const clearing = "clearing";
    const oneOff = "One-off";
    const cash = "cash";
    
    const compairedsyndicateEndDate = this.getTimeDiff(trimmedEndDate);
    
    return (
      syndicate?.current_status == clearing &&
      syndicate?.syndicate_type == oneOff &&
      syndicate?.offer_details?.offer_currency == cash &&
      compairedsyndicateEndDate
    );
  }

  getTimeDiff(synEndDate: string): boolean {
    // Comprehensive guard clause: check for undefined, null, non-string, and empty string
    // This prevents the TypeError when synEndDate is undefined/null
    if (synEndDate === undefined || synEndDate === null || typeof synEndDate !== 'string') {
      return false;
    }
    
    // Additional check for empty or whitespace-only strings
    const trimmedDate = synEndDate.trim();
    if (trimmedDate === '') {
      return false;
    }
    
    try {
      // Now that we've confirmed synEndDate is a valid non-empty string,
      // call replace() directly
      const dates = trimmedDate.replace("AM", "").replace("PM", "");
      
      // Parse the target date as a UTC date
      const targetDate = new Date(dates);
      
      // Check if the parsed date is valid
      if (isNaN(targetDate.getTime())) {
        return false;
      }
      
      // Get current time and convert it to UTC time
      const currentTime = new Date();
      
      // Calculate the difference in milliseconds
      const timeDifference = currentTime.getTime() - targetDate.getTime();
      
      // Ensure the difference is positive
      if (timeDifference > 0) {
        return false;
      } else {
        return true;
      }
    } catch (error) {
      // Catch any unexpected errors during date processing
      this.authService.errorHandlerLogs("Error in getTimeDiff:", error);
      return false;
    }
  }

  // Get syndicate product name
  getSyndicateProductName(products: any) {
    let productName: string = "";
    if (products) {
      products?.map((data) => {
        if (productName?.indexOf(data?.product_name) == -1) {
          if (productName == "") productName = productName + data?.product_name;
          else productName = productName + ", " + data?.product_name;
        }
        // productName = productName + data.product_name;
      });
    }
    return productName;
  }

  // Get syndicate card height for styling purpose
  getSyndicateCardHeight(productDetails: any) {
    let productLength = 0;
    try {
      productLength += productDetails?.products.map((draw) => draw?.draw_day?.length);
      if (productDetails?.cardType?.toLowerCase() === "private") {
        /* All measures are in vh */
        const defaultLength = 8;

        if (!productDetails?.products) {
          return defaultLength;
        }

        let requiredHeight = productDetails?.products?.length ? productLength * defaultLength + defaultLength : 0;
        return requiredHeight;
      }
    } catch (e) {
      return e;
    }
    if (productDetails?.syndicate_type_card === "MANAGED") {
      /* All measures are in vh */
      const defaultLength = 8;
      if (!productDetails?.product) {
        return defaultLength;
      }
      try {
        let requiredHeight = productDetails?.product?.length ? productLength * defaultLength + defaultLength : 0;

        /* Managed card doesn't require friend list, count and add players function */
        requiredHeight = requiredHeight - 8;

        return requiredHeight;
      } catch (e) {
        return e;
      }
    }
  }

  // This method is to open syndicate setting modal on click of each syndicate kebab menu
  openSyndicateSetting(syndicate) {
    this.openSyndicateSettingEvent.emit(syndicate);
  }

  // This function is used to update the product details of the syndicate
  // This function takes the syndicate details as the input
  // This function returns syndicate details
  updateProductForManagedSyndicate(syndicateDetails: Data) {
    this.updateProductForManagedSyndicateEvent.emit(syndicateDetails);
  }

  clickOfferBanner(syndicate: Data): void {
    this.clickOfferBannerEvent.emit(syndicate);
  }

  openTickets(syndicate: any, clickAction: any) {
    this.openTicketsEvent.emit({ syndicate: syndicate, clickAction: clickAction });
  }

  routeToCreateSyndicate(syndicate) {
    this.routeToCreateSyndicateEvent.emit(syndicate);
  }

  getPrivateSyndicate(syndicate, prdIndex) {
    this.getPrivateSyndicateEvent.emit({ syndicate: syndicate, prdIndex: prdIndex });
  }

  showPaymentOptions(syndicate) {
    this.showPaymentOptionsEvent.emit(syndicate);
  }

  chatNow(syndicate) {
    this.chatNowEvent.emit(syndicate);
  }

  openSyndicateMembers(syndicateMemberData) {
    this.openSyndicateMembersEvent.emit(syndicateMemberData);
  }

  // Get syndicate lines count
  getSyndicateLinesCount(products: any) {
    let count = 0;
    if (products) {
      products?.map((data) => {
        count += data?.total_lines;
      });
    }
    return count;
  }

  // Tooltip
  showTooltip(syndicate): boolean {
    if (!syndicate || !syndicate?.current_status) return false;

    const current_status = ["running", "paused", "stopped", "pending", "holiday"];
    return current_status.includes(syndicate.current_status) && syndicate.current_status !== "clearing";
  }

  //Status translation
  getStatusTranslation(status: string): string {
    if (!status) return "";

    const statusMapping = new Map<string, string>([
      ["running", "dynamic_value.current_status_running"],
      ["paused", "dynamic_value.current_status_paused"],
      ["stopped", "dynamic_value.current_status_stopped"],
      ["pending", "dynamic_value.current_status_pending"],
      ["holiday", "dynamic_value.current_status_holiday"],
    ]);

    const translteKey = statusMapping.get(status);
    return translteKey ? this.translateService.instant(translteKey) : "";
  }

  // This method is used to format the data for displaying purpose.
  formatDate(synDate: string | Date | null | undefined) {
    // Comprehensive guard clause: check for undefined, null, non-string, and empty string
    if (synDate === undefined || synDate === null || typeof synDate !== 'string') {
      return "N/A";
    }
    
    // Additional check for empty or whitespace-only strings
    const trimmedDate = synDate.trim();
    if (trimmedDate === '') {
      return "N/A";
    }
    
    try {
      var dates = trimmedDate.replace("AM", "").replace("PM", "");
      var dateObj = new Date(dates);
      
      // Check if the parsed date is valid
      if (isNaN(dateObj.getTime())) {
        return "N/A";
      }
      
      var date = dateObj?.getDate();
      var sup = "";
      const month = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ][dateObj.getMonth()];

      if (date % 10 == 1) sup = "st";
      else if (date % 10 == 2) sup = "nd";
      else if (date % 10 == 3) sup = "rd";
      else sup = "th";

      if (date > 3 && date < 21) sup = "th";
      return `${date}${sup} ${month}`;
    } catch (error) {
      this.authService.errorHandlerLogs("Error in formatDate:", error);
      return "N/A";
    }
  }

  getTimeFormate(synDate: string) {
    // Comprehensive guard clause: check for undefined, null, non-string, and empty string
    if (synDate === undefined || synDate === null || typeof synDate !== 'string') {
      return "Expired";
    }
    
    // Additional check for empty or whitespace-only strings
    const trimmedDate = synDate.trim();
    if (trimmedDate === '') {
      return "Expired";
    }
    
    try {
      const dates = trimmedDate.replace("AM", "").replace("PM", "");
      // Parse the target date as a UTC date
      const targetDate = new Date(dates);
      
      // Check if the parsed date is valid
      if (isNaN(targetDate.getTime())) {
        return "Expired";
      }
      
      // Get current time and convert it to UTC time
      const currentTime = new Date();
      const utcCurrentTime = new Date(currentTime.getTime() + currentTime.getTimezoneOffset() * 60000);
      // Calculate the difference in milliseconds
      let timeDifference = targetDate.getTime() - utcCurrentTime.getTime();
      // Ensure the difference is positive
      if (timeDifference > 0) {
        // Convert milliseconds difference to days
        const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
        timeDifference -= days * (1000 * 60 * 60 * 24); // Remove the days from timeDifference
        // Convert the remaining milliseconds to hours
        const hours = Math.floor(timeDifference / (1000 * 60 * 60)) - 5;
        timeDifference -= hours * (1000 * 60 * 60); // Remove the hours from timeDifference
        // Return the result string
        const result = `${days}d ${hours}h`;
        return result;
      } else {
        return "Expired";
      }
    } catch (error) {
      this.authService.errorHandlerLogs("Error in getTimeFormate:", error);
      return "Expired";
    }
  }

  // to format date with specified format
  formatDatewithOrdinal(date: Data) {
    return moment(date).format("Do MMMM");
  }

  // Get product lines
  getPSLines(products) {
    let count = 0;
    if (products) {
      try {
        products?.map((product: Data) => {
          count += product?.total_lines * product?.draw_day?.length;
        });
      } catch (e) {
        return e;
      }
    }

    return count;
  }

  getTranslatedProductName(productName: string): string {
    if (!productName) return "Loading..";

    const translatedValue = this.translateService.instant("dynamic_value." + productName);
    return translatedValue.includes("dynamic_value") ? productName : translatedValue;
  }
}